import bowser from 'yola-bowser';
import MutationDriver from './utils/classes/mutation-driver';
import randomizeWsElementsIds from './utils/helpers/randomize-ws-elements-ids';
import bindings from './injections/bindings';
import customEventListeners from './listeners/custom-event-listeners';
import eventListeners from './listeners/event-listeners';
import blurListener from './listeners/blur-listener';
import clickListener from './listeners/click-listener';
import parentContextClickListener from './listeners/parent-context-click-listener';
import keyDownListener from './listeners/keydown-listener';
import keyUpListener from './listeners/key-up-listener';
import cutListener from './listeners/cut-listener';
import copyListener from './listeners/copy-listener';
import pasteListener from './listeners/paste-listener';
import scrollListener from './listeners/scroll-listener';
import selectionChangeListener from './listeners/selection-change-listener';
import mouseDownListener from './listeners/mouse-down-listener';
import mouseUpListener from './listeners/mouse-up-listener';
import inputListener from './listeners/input-listener';
import customTouchEventsListener from './listeners/custom-touch-events-listener';
import dragstartListener from './listeners/dragstart-listener';
import addWidgetListener from './listeners/add-widget-listener';
import extensions from '../extensions';
import globals from '../globals';
import hooks from '../hooks';
import customHtml from '../custom-html';

import elementFocusHandler from './handlers/element-focus-handler';
import elementSelectHandler from './handlers/element-select-handler';
import elementHoverHandler from './handlers/element-hover-handler';
import windowLoadHandler from './handlers/window-load-handler';
import scrollHandler from './handlers/scroll-handler';
import elementTapHandler from './handlers/element-tap-handler';

import iOSCropListener from './injections/iframe-additives/ios-workarounds/crop-listener';
import fixIOSWindowInnerHeight from './injections/iframe-additives/ios-workarounds/fix-window-inner-height';
import replaceIOSVHUnits from './injections/iframe-additives/ios-workarounds/replace-vh-units';
import preventIOSElasticScrollPropagation from './injections/iframe-additives/ios-workarounds/prevent-ios-elastic-scroll-propagation';

import contenteditableFocus from './injections/iframe-additives/webkit-workarounds/contenteditable-focus';

import disableVariablesTextEditing from '../site/helpers/disable-variables-text-editing';
import disablePageSettingsHTMLCode from '../site/helpers/disable-page-settings-html-code';

import iPadOSScrollPositionWatcher from './injections/iframe-additives/ios-workarounds/ipad-os-scroll-position-watcher';
import surfacesAttrChangeObserver from './injections/iframe-additives/surfaces-attr-change-observer';
import removeLazyLoadingAttributeFromImages from './utils/helpers/webkit-workarounds/remove-lazy-loading-attribute-from-images';
import updateBlockId from './utils/helpers/update-block-id';

const config = {
  referenceAttribute: 'data-ws-id',
  customInjections: [bindings.text, bindings.mutation],
  onBeforeInsertStaticElement(staticElement) {
    if (staticElement.id) {
      // eslint-disable-next-line no-param-reassign
      staticElement.id = updateBlockId(staticElement.id);
    }
    randomizeWsElementsIds(staticElement);
    extensions.helpers.applyInjection('beforeElementInsert', staticElement);

    hooks.handlers.onBeforeInsertStaticElement(staticElement);
  },
  onStaticDocumentConnected(staticDOM) {
    globals.helpers.injectGlobals(staticDOM);
    customHtml.helpers.injectCustomHtml(staticDOM);
    extensions.helpers.applyInjection('beforeViewLoad', staticDOM);
    disablePageSettingsHTMLCode(staticDOM);

    hooks.handlers.onStaticDocumentConnected(staticDOM);
  },
  onAfterInsertStaticElement(staticElement) {
    extensions.helpers.applyInjection('afterStaticElementInsert', staticElement);

    hooks.handlers.onAfterInsertStaticElement(staticElement);
  },
  onBeforeDuplicateStaticElement(staticElement) {
    if (staticElement.id) {
      // eslint-disable-next-line no-param-reassign
      staticElement.id = updateBlockId(staticElement.id);
    }

    hooks.handlers.onBeforeDuplicateStaticElement(staticElement);
  },
  onAfterDuplicateStaticElement(staticElement) {
    extensions.helpers.applyInjection('afterDuplicateStaticElement', staticElement);

    hooks.handlers.onAfterDuplicateStaticElement(staticElement);
  },
  onAfterInsertLiveElement(liveElement) {
    extensions.helpers.applyInjection(
      'afterElementInsert',
      disableVariablesTextEditing(liveElement)
    );
    contenteditableFocus.pauseAll(liveElement);

    hooks.handlers.onAfterInsertLiveElement(liveElement);
  },
  onAfterShiftStaticElement(staticElement) {
    extensions.helpers.applyInjection('afterShiftStaticElement', staticElement);

    hooks.handlers.onAfterShiftStaticElement(staticElement);
  },
  onAfterReplaceLiveElement(oldLiveElement, newLiveElement) {
    extensions.helpers.applyInjection(
      'afterElementInsert',
      disableVariablesTextEditing(newLiveElement)
    );
    contenteditableFocus.pauseAll(newLiveElement);

    hooks.handlers.onAfterReplaceLiveElement(oldLiveElement, newLiveElement);
  },
  onAfterSynchronizeStaticElement(transformedElement) {
    extensions.helpers.applyInjection('afterElementSynchronize', transformedElement);

    hooks.handlers.onAfterSynchronizeStaticElement(transformedElement);
  },
  onLiveDocumentComposed(liveDocument) {
    hooks.handlers.onLiveDocumentComposed(liveDocument);
  },
  onLiveDocumentConnected(liveDocument) {
    // Do nothing if liveDocument is not rendered
    if (!liveDocument.defaultView) return;

    // IOS workarounds
    const {
      defaultView: { frameElement },
    } = liveDocument;
    fixIOSWindowInnerHeight(liveDocument.defaultView);
    replaceIOSVHUnits(liveDocument);
    iOSCropListener.connect(frameElement);

    // initialize iPadOS workaround
    iPadOSScrollPositionWatcher(liveDocument, frameElement);

    eventListeners.add(liveDocument.body, 'touchmove', preventIOSElasticScrollPropagation, {
      passive: false,
    });

    // Webkit workarounds
    contenteditableFocus.pauseAll(liveDocument);

    customEventListeners.reset();
    customEventListeners.watch(liveDocument.defaultView);

    eventListeners.add(liveDocument, 'focus', elementFocusHandler, true);

    if (bowser.mobile || bowser.tablet) {
      customTouchEventsListener(liveDocument);
      eventListeners.add(liveDocument, 'ws:tap', elementTapHandler);
    } else {
      eventListeners.add(liveDocument, 'mousedown', elementSelectHandler);
      eventListeners.add(liveDocument.body, 'mousemove', elementHoverHandler, { passive: true });
      eventListeners.add(liveDocument, 'mousedown', contenteditableFocus.resumeByEvent);
    }

    eventListeners.add(liveDocument.defaultView, 'load', windowLoadHandler);
    blurListener(liveDocument);
    clickListener(liveDocument);
    parentContextClickListener(liveDocument);
    keyDownListener(liveDocument);
    keyUpListener(liveDocument);
    copyListener(liveDocument);
    cutListener(liveDocument);
    pasteListener(liveDocument);
    mouseDownListener(liveDocument);
    mouseUpListener(liveDocument);
    selectionChangeListener(liveDocument);
    inputListener(liveDocument);
    scrollListener.add(liveDocument, scrollHandler);
    dragstartListener(liveDocument);
    addWidgetListener(liveDocument);

    // Extensions' injections
    extensions.helpers.applyInjection('afterViewLoad', disableVariablesTextEditing(liveDocument));

    // Site load injection
    // eslint-disable-next-line no-underscore-dangle
    const isCustomDocumentLoadDefined = liveDocument.defaultView.__custom_load_event__;

    if (isCustomDocumentLoadDefined) {
      eventListeners.add(liveDocument, 'WSSiteLoaded', hooks.handlers.onSiteLoaded);
    } else {
      eventListeners.add(liveDocument.defaultView, 'load', hooks.handlers.onSiteLoaded);
    }

    // Elements mutation observers injections
    surfacesAttrChangeObserver(liveDocument);

    hooks.handlers.onLiveDocumentConnected(liveDocument);
  },
  onBeforeSynchronizeStaticElement(oldElement, newElement) {
    hooks.handlers.onBeforeSynchronizeStaticElement(oldElement, newElement);
  },
  onBeforeSynchronizeLiveElement(element) {
    hooks.handlers.onBeforeSynchronizeLiveElement(element);
  },
  onBeforeSetStaticElementAttribute(staticElement, attribute, value) {
    hooks.handlers.onBeforeSetStaticElementAttribute(staticElement, attribute, value);
  },
  onBeforeSetLiveElementAttribute(staticElement, attribute, value) {
    hooks.handlers.onBeforeSetLiveElementAttribute(staticElement, attribute, value);
  },
  onBeforeReplaceStaticElement(oldElement, newElement) {
    hooks.handlers.onBeforeReplaceStaticElement(oldElement, newElement);
  },
  onBeforeReplaceLiveElement(oldElement, newElement) {
    hooks.handlers.onBeforeReplaceLiveElement(oldElement, newElement);
  },
  onBeforeInsertLiveElement(element) {
    removeLazyLoadingAttributeFromImages(element);
    hooks.handlers.onBeforeInsertLiveElement(element);
  },
};

const driver = new MutationDriver(config);

export default {
  config,
  driver,
};

// Assign to global scope in order to be able to use domma from iframe context
// eslint-disable-next-line no-underscore-dangle
window.__driver = driver;
